import React, { useState, useRef, useEffect } from 'react';
import { IoSend, IoClose, IoChevronDown, IoFlash, IoSearchOutline, IoListOutline } from 'react-icons/io5';
import { CText } from '../../../components/common';

interface ChatInputProps {
  onSend: (message: string, options: ChatOptions) => void;
  isLoading: boolean;
  selectedCompanies: string[];
  onCompanySelect: (companies: string[]) => void;
  allCompanies: string[];
  colors: any;
  placeholder?: string;
}

interface ChatOptions {
  deepSearch: boolean;
  multiAgent: boolean;
  topK: number;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  isLoading,
  selectedCompanies,
  onCompanySelect,
  allCompanies,
  colors,
  placeholder = 'Ask anything about stocks...',
}) => {
  const [message, setMessage] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [deepSearch, setDeepSearch] = useState(false);
  const [multiAgent, setMultiAgent] = useState(true);
  const [topK, setTopK] = useState<number>(5);
  const [showTopKDropdown, setShowTopKDropdown] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const topKDropdownRef = useRef<HTMLDivElement>(null);

  const topKOptions = [3, 5, 10, 15, 20];

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  }, [message]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowCompanyDropdown(false);
      }
      if (topKDropdownRef.current && !topKDropdownRef.current.contains(event.target as Node)) {
        setShowTopKDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSend = () => {
    if (message.trim() && !isLoading) {
      onSend(message.trim(), { deepSearch, multiAgent, topK });
      setMessage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const filteredCompanies = allCompanies.filter(company =>
    company.toLowerCase().includes(companySearch.toLowerCase())
  );

  const toggleCompany = (company: string) => {
    if (selectedCompanies.includes(company)) {
      onCompanySelect(selectedCompanies.filter(c => c !== company));
    } else {
      onCompanySelect([...selectedCompanies, company]);
    }
  };

  return (
    <div
      className="p-4"
      style={{
        backgroundColor: colors.inputBackground,
        borderTop: `1px solid ${colors.borderColor}`,
      }}
    >
      {/* Selected Companies Tags */}
      {selectedCompanies.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {selectedCompanies.map(company => (
            <span
              key={company}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-sm"
              style={{
                backgroundColor: colors.primary + '20',
                color: colors.primary,
              }}
            >
              {company}
              <button
                onClick={() => toggleCompany(company)}
                className="hover:opacity-70"
              >
                {React.createElement(IoClose as any, { size: 14 })}
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Options Row */}
      <div className="flex items-center gap-3 mb-3">
        {/* Company Selector */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: colors.backgroundColor,
              border: `1px solid ${colors.borderColor}`,
            }}
          >
            <CText type="r12" color={colors.grayScale500}>
              {selectedCompanies.length > 0
                ? `${selectedCompanies.length} selected`
                : 'All Companies'}
            </CText>
            {React.createElement(IoChevronDown as any, { size: 14, color: colors.grayScale500 })}
          </button>

          {/* Dropdown */}
          {showCompanyDropdown && (
            <div
              className="absolute bottom-full mb-2 left-0 w-64 max-h-64 overflow-hidden rounded-lg shadow-lg z-50"
              style={{
                backgroundColor: colors.backgroundColor,
                border: `1px solid ${colors.borderColor}`,
              }}
            >
              {/* Search */}
              <div className="p-2" style={{ borderBottom: `1px solid ${colors.borderColor}` }}>
                <div
                  className="flex items-center gap-2 px-2 py-1 rounded"
                  style={{ backgroundColor: colors.inputBackground }}
                >
                  {React.createElement(IoSearchOutline as any, { size: 14, color: colors.grayScale400 })}
                  <input
                    type="text"
                    value={companySearch}
                    onChange={(e) => setCompanySearch(e.target.value)}
                    placeholder="Search companies..."
                    className="flex-1 bg-transparent text-sm outline-none"
                    style={{ color: colors.textColor }}
                  />
                </div>
              </div>

              {/* Company List */}
              <div className="max-h-48 overflow-y-auto">
                {filteredCompanies.slice(0, 50).map(company => (
                  <div
                    key={company}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:opacity-80"
                    style={{
                      backgroundColor: selectedCompanies.includes(company)
                        ? colors.primary + '10'
                        : 'transparent',
                    }}
                    onClick={() => toggleCompany(company)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCompanies.includes(company)}
                      onChange={() => {}}
                      style={{ accentColor: colors.primary }}
                    />
                    <CText type="r12" color={colors.textColor}>
                      {company}
                    </CText>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Toggle Options */}
        <button
          onClick={() => setDeepSearch(!deepSearch)}
          className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors`}
          style={{
            backgroundColor: deepSearch ? colors.primary + '20' : colors.backgroundColor,
            border: `1px solid ${deepSearch ? colors.primary : colors.borderColor}`,
          }}
          title="Deep Search: Analyze full PDF documents"
        >
          {React.createElement(IoSearchOutline as any, {
            size: 14,
            color: deepSearch ? colors.primary : colors.grayScale500,
          })}
          <CText type="r12" color={deepSearch ? colors.primary : colors.grayScale500}>
            Deep
          </CText>
        </button>

        <button
          onClick={() => setMultiAgent(!multiAgent)}
          className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors`}
          style={{
            backgroundColor: multiAgent ? colors.primary + '20' : colors.backgroundColor,
            border: `1px solid ${multiAgent ? colors.primary : colors.borderColor}`,
          }}
          title="Multi-Agent: Use web search for latest data"
        >
          {React.createElement(IoFlash as any, {
            size: 14,
            color: multiAgent ? colors.primary : colors.grayScale500,
          })}
          <CText type="r12" color={multiAgent ? colors.primary : colors.grayScale500}>
            Multi-Agent
          </CText>
        </button>

        {/* Top K Selector */}
        <div className="relative" ref={topKDropdownRef}>
          <button
            onClick={() => setShowTopKDropdown(!showTopKDropdown)}
            className="flex items-center gap-1 px-3 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: colors.backgroundColor,
              border: `1px solid ${colors.borderColor}`,
            }}
            title="Number of results to retrieve"
          >
            {React.createElement(IoListOutline as any, {
              size: 14,
              color: colors.grayScale500,
            })}
            <CText type="r12" color={colors.grayScale500}>
              {topK} results
            </CText>
            {React.createElement(IoChevronDown as any, { size: 12, color: colors.grayScale500 })}
          </button>

          {/* TopK Dropdown */}
          {showTopKDropdown && (
            <div
              className="absolute bottom-full mb-2 left-0 w-32 rounded-lg shadow-lg z-50 overflow-hidden"
              style={{
                backgroundColor: colors.backgroundColor,
                border: `1px solid ${colors.borderColor}`,
              }}
            >
              {topKOptions.map(option => (
                <div
                  key={option}
                  className="px-3 py-2 cursor-pointer hover:opacity-80"
                  style={{
                    backgroundColor: topK === option ? colors.primary + '10' : 'transparent',
                  }}
                  onClick={() => {
                    setTopK(option);
                    setShowTopKDropdown(false);
                  }}
                >
                  <CText
                    type="r12"
                    color={topK === option ? colors.primary : colors.textColor}
                  >
                    {option} results
                  </CText>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Input Row */}
      <div
        className="flex items-end gap-3 rounded-xl p-2"
        style={{
          backgroundColor: colors.backgroundColor,
          border: `1px solid ${colors.borderColor}`,
        }}
      >
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1 resize-none outline-none text-sm"
          style={{
            backgroundColor: 'transparent',
            color: colors.textColor,
            minHeight: 24,
            maxHeight: 150,
          }}
          rows={1}
          disabled={isLoading}
        />

        <button
          onClick={handleSend}
          disabled={!message.trim() || isLoading}
          className="p-2 rounded-lg transition-colors flex-shrink-0"
          style={{
            backgroundColor: message.trim() && !isLoading ? colors.primary : colors.grayScale400,
            opacity: message.trim() && !isLoading ? 1 : 0.5,
          }}
        >
          {React.createElement(IoSend as any, { size: 18, color: colors.white })}
        </button>
      </div>
    </div>
  );
};

export default ChatInput;
